import { useCookieContext } from "contexts";
import { useEffect, useRef, useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import ReactSelect from "react-select";
import CreatableSelect from "react-select/creatable";
import { BackendServices } from "services";
import GridFilter from "../GridView/components/GridFilter";
import {DataGridRequest, SharedFilterRequest} from "../../services/shared-models/sharedTypes";

export interface DropdownItemType extends Record<string, any> {
  value: string;
  label: string;
}

type PaginatedLookupDropdownProps<T extends keyof typeof BackendServices> = {
  service: T;
  endpoint: keyof (typeof BackendServices)[T];
  onError?: (error: any) => void;
  textValueKey: string;
  idValueKey: string;
  isPaginated?: boolean;
  customFilter?:SharedFilterRequest[] 
  disabled?: boolean;
  value?: string | number | string[];
  noSelectionPlaceholder?: string;
  wrapperClassName?: string;
  label?: string;
  queryKey: string;
  error?: string;
  multiselect?: boolean;
  labelClassName?: string;
  isCreatable?: boolean;
  withAsterisk?: boolean;
  defaultOptions?: DropdownItemType[] | null;
  filterOption?: (option: DropdownItemType, inputValue: string, data: any) => boolean;
  validateCreatedOption?: (inputValue: string) => boolean;
  onDropdownReady?: (items: DropdownItemType[]) => void;
} & (
  | ({ multiselect?: false } & {
      onChange?: (value: string, item: DropdownItemType, options: any[]) => void;
      value?: string | number;
    })
  | ({ multiselect: true } & { onChange?: (value: string[], options: any[]) => void; value?: string[] })
);

// TODO: Arrow icon coloring (primary)
const access = (path: string, object: any) => path.split(".").reduce((o, i) => o[i], object);

function PaginatedLookupDropdown<T extends keyof typeof BackendServices>(props: PaginatedLookupDropdownProps<T>) {
  const {
    endpoint,
    textValueKey,
    idValueKey,
    onError,
    service,
    isPaginated,
    disabled,
    queryKey,
    label,
    noSelectionPlaceholder,
    wrapperClassName,
    onChange,
    value,
    error,
    multiselect,
    defaultOptions,
    labelClassName,
    filterOption,
    onDropdownReady,
    withAsterisk,
    customFilter
  } = props;

  const debounceSearchRef = useRef<NodeJS.Timeout>();
  const isInitialLoad = useRef(true);
  const dropdownReady = useRef(false);
  const selectedValues = useRef<{ [key: string]: DropdownItemType }>({});
  const [data, setData] = useState<any>();
  const [options, setOptions] = useState<DropdownItemType[]>([]);
  const { isAr, isRTL } = useCookieContext();

  const onLocalChange = (e: SingleValue<DropdownItemType>) => {
    !multiselect && onChange?.(`${e?.value}`, { value: `${e?.value}`, label: e?.label ?? "" }, data);
  };

  const onMultiLocalChange = (e: MultiValue<DropdownItemType>) => {
    const values = e.map((item) => item.value);

    // Update selectedValues.current to preserve selected items when fetching new data
    e.forEach((item) => {
      selectedValues.current[item.value] = item;
    });

    // Remove unselected items from selectedValues.current
    Object.keys(selectedValues.current).forEach((key) => {
      if (!values.includes(key)) {
        delete selectedValues.current[key];
      }
    });

    multiselect && onChange?.(values, data);
  };

  const getData = async (inputValue: string) => {
    let queryParams :any = isPaginated
      ? { [queryKey]: inputValue, pageIndex: 1, pageSize: 10 }
      : { [queryKey]: inputValue };
    
    if(customFilter){
      queryParams = {... queryParams, filter: customFilter };
    }

    const res = await (BackendServices[service][endpoint] as any)(queryParams);
    if (!res.hasError && res.data ) {
      const responseData = isPaginated ? res.data.result : res.data;

      const items = responseData.map((item: any) => ({
        value: access(idValueKey, item),
        label: access(textValueKey, item),
      }));

      Object.keys(selectedValues.current).forEach((key) => {
        if (!items.find((item: any) => `${access(idValueKey, item)}` === key)) {
          responseData.push(selectedValues.current[key]);
          items.push(selectedValues.current[key]);
        }
      });

      setData(responseData);
      setOptions(items);

      if (!dropdownReady.current) {
        onDropdownReady?.(items);
        dropdownReady.current = true;
      }
      return items;
    } else {
      onError?.(res.description);
      return [];
    }
  };

  useEffect(() => {
    getData("");
  }, [isAr]);

  const Select = props.isCreatable ? CreatableSelect : ReactSelect;

  useEffect(() => {
    if (isInitialLoad.current && defaultOptions) {
      isInitialLoad.current = false;

      if (props.isCreatable) {
        const newOptions = options.concat(
          defaultOptions.filter((item) => !options.find((option) => option.label === item.label)),
        );
        setOptions(newOptions);
      } else {
        setOptions(
          options.concat(defaultOptions.filter((item) => !options.find((option) => option.value === item.value))),
        );
      }
    }
  }, [options, defaultOptions]);

  const onCreateOption = (value: string) => {
    if (props.validateCreatedOption) {
      const isValid = props.validateCreatedOption(value);
      if (!isValid) {
        // refetch data to remove the created option
        getData("");
        return;
      }
    }

    selectedValues.current[value] = { value, label: value };
    setOptions([...options, { value, label: value }]);

    if (multiselect) {
      const oldSelectedValues = [];
      for (const key in selectedValues.current) {
        if (props.value?.includes(key)) {
          oldSelectedValues.push(selectedValues.current[key]);
        }
      }
      onMultiLocalChange([...oldSelectedValues, { value, label: value }]);
    } else {
      onLocalChange({ value, label: value });
    }
  };

  const hasError = !!error;

  return (
    <div className={`${wrapperClassName}`} style={{ minWidth: 150 }}>
      {label && (
        <div className={`mb-1 ${labelClassName} position-relative`}>
          {typeof label === "string" ? <label className={`${withAsterisk ? "px-2" : ""}`}>{label}</label> : label}
          {withAsterisk && (
              <span
                  className={` text-danger`}
                  style={{
                    fontSize: "0.75rem",
                  }}
              >
              *
            </span>
          )}
        </div>
      )}
      <Select
        isMulti={!!multiselect}
        placeholder={noSelectionPlaceholder ?? label}
        isDisabled={disabled}
        menuPortalTarget={document.body}
        value={
          multiselect
            ? !props.isCreatable
              ? value?.map((ele) => options.find((item) => item.value === ele))
              : options.filter((item) => value?.includes(item.label))
            : !props.isCreatable
            ? options.find((item) => item.value === value)
            : options.find((item) => item.label === value)
        }
        styles={{
          control: (styles) => ({
            ...styles,
            borderColor: hasError ? "red" : styles.borderColor,
            backgroundColor: hasError ? "#f8d7da" : styles.backgroundColor,
            height: "42px",
          }),
          singleValue: (styles) => ({
            ...styles,
            /*  ...(phoneNumberSelect &&
              isRTL && {
                direction: "ltr",
                textAlign: "right",
              }), */
          }),
          menuList: (styles) => ({
            ...styles,
            /*  ...(phoneNumberSelect &&
              isRTL && {
                direction: "ltr",
                textAlign: "right",
              }), */
          }),
          menuPortal: (styles) => ({
            ...styles,
            zIndex: 2500, // Ensure dropdown renders above all other elements
          }),
        }}
        // loadOptions={props.isCreatable ? getData : undefined}
        options={options}
        onCreateOption={onCreateOption}
        filterOption={(option, inputValue) => {
          if (filterOption) {
            return filterOption(option, inputValue, data);
          }

          return true;
        }}
        onChange={(e) => {
          if (multiselect) {
            onMultiLocalChange(e as MultiValue<DropdownItemType>);
          } else {
            onLocalChange(e as SingleValue<DropdownItemType>);
          }
        }}
        onInputChange={(inputValue) => {
          if (debounceSearchRef.current) {
            clearTimeout(debounceSearchRef.current);
          }

          debounceSearchRef.current = setTimeout(() => {
            getData(inputValue);
          }, 500);
        }}
      />
      {hasError && (
        <div className="invalid-feedback" style={{ display: "block" }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default PaginatedLookupDropdown;
